import { useEffect, useState } from 'react';
import { getConfig } from '../../utils/config';
import AgreementBody from '../agreements/AgreementBody';
import StandalonePageLayout from '../info-pages/layout/StandalonePageLayout';
import { PageOpening, Section, Ledger, MarginNote } from '../public/Journal';

// Typography for the agreement body, adapted to paper and ink. AgreementBody
// is shared with the dashboard agreement gate, so styling goes in via className.
const bodyClass = [
  'prose-base font-plex text-ink/80 dark:text-paper/80',
  'prose-headings:font-news prose-headings:font-normal prose-headings:tracking-[-0.01em] prose-headings:text-ink dark:prose-headings:text-paper',
  'prose-h1:text-[34px] prose-h2:text-[28px] prose-h2:mt-12 prose-h3:text-[22px]',
  'prose-p:leading-[1.8] prose-li:leading-[1.75] prose-li:marker:text-stamp',
  'prose-strong:text-ink dark:prose-strong:text-paper',
  'prose-a:text-stamp prose-a:underline-offset-2',
  'prose-hr:border-ink/15 dark:prose-hr:border-paper/15',
  'prose-blockquote:border-l-stamp prose-blockquote:font-news prose-blockquote:not-italic',
].join(' ');

export default function TermsConditions() {
  const [agreement, setAgreement] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_URL = getConfig('API_URL');
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/public/agreements/terms_of_service`);
        if (!resp.ok) throw new Error('not ok');
        const data = await resp.json();
        setAgreement(data);
      } catch {
        setError('Unable to load Terms of Service. Please try again later or contact support.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Date-only strings ("2026-01-15") parse as UTC midnight and render a day
  // early west of UTC, so read the calendar date as local.
  const effective = agreement?.effective_date
    ? (/^\d{4}-\d{2}-\d{2}$/.test(agreement.effective_date)
        ? new Date(`${agreement.effective_date}T00:00:00`)
        : new Date(agreement.effective_date)
      ).toLocaleDateString()
    : '—';

  return (
    <StandalonePageLayout>
      <PageOpening
        section="§ Terms"
        label="Legal"
        title="Terms of Service."
        intro="The agreement that governs your use of JPRO, published in full."
      >
        {agreement && (
          <Ledger
            className="max-w-xl"
            rows={[
              ['Document', agreement.name],
              ['Effective', <span key="d" className="font-plex-mono text-[15px]">{effective}</span>],
              ['Version', <span key="v" className="font-plex-mono text-[15px]">{agreement.version_number}</span>],
            ]}
          />
        )}
      </PageOpening>

      <Section
        number="01"
        label="The agreement"
        aside={agreement && <MarginNote>Version {agreement.version_number}, effective {effective}.</MarginNote>}
      >
        {loading && (
          <p className="border-y border-ink/15 py-6 font-plex-mono text-[13px] text-ink/60 dark:border-paper/15 dark:text-paper/60">
            Loading the agreement<span className="animate-pulse">…</span>
          </p>
        )}
        {error && (
          <div className="border-y border-ink/15 py-8 dark:border-paper/15">
            <p className="font-plex-mono text-[11.5px] uppercase tracking-[0.18em] text-stamp">Not available</p>
            <p className="mt-3 max-w-2xl font-news text-[26px] leading-snug">{error}</p>
            <a
              href="mailto:jpro@zeffert.com"
              className="link-underline mt-5 inline-block font-plex-mono text-[12px] uppercase tracking-[0.16em] text-stamp"
            >
              Contact support
            </a>
          </div>
        )}
        {agreement && (
          <article className="max-w-3xl">
            <AgreementBody markdown={agreement.body_markdown} className={bodyClass} />
          </article>
        )}
      </Section>
    </StandalonePageLayout>
  );
}
